// fab.ts — the SDK-injected Backlit floating action button (FAB).
//
// On load (initFAB, called from index.ts at module eval) the SDK injects a small
// floating button bottom-right on every Lit glow — the Backlit mark on a dark
// disc with an amber glow behind it. Clicking it opens a panel
// with a "Get your own glow" link, the signed-in identity + a Sign-out button,
// and — on a glow that offers self-serve signin (Accounts mode) — a Sign-in
// button when signed out. Removable only on a paid plan (CLAUDE.md §8).
//
// Hardening: rendered inside a CLOSED shadow root (host-page CSS can't bleed in
// or out; host script can't reach the tree). All dynamic strings (the user's
// name/email) go through textContent, NEVER innerHTML — those values are
// user-controlled (an Accounts visitor picks their own display name), so this is
// the stored-XSS boundary. The marketing URL was already validated to https: by
// the transport decoder.
//
// Best-effort everywhere: any failure (no DOM, off-host, bootstrap error) simply
// yields no FAB; nothing throws.

import { isBacklitHost } from "./host.js";
import { getBackend, type UIConfig } from "./backend.js";

const FAB_ROOT_ID = "backlit-fab-root";

// hideRequested records a hideUI() call. It wins the race against the async
// bootstrap: if a (paid) author calls hideUI() before the FAB injects, render()
// sees the flag and skips injection. A free author's call is a no-op (the gate
// is checked against canHideBranding below).
let hideRequested = false;
// lastCanHide caches the bootstrap's can_hide_branding so a later hideUI() can
// honour the paid gate without re-fetching. Defaults false (fail-closed: a free
// or not-yet-resolved author cannot remove the FAB).
let lastCanHide = false;

// Navigation callbacks the FAB buttons invoke, injected by index.ts (the
// in-scope backlit.auth instance) — NOT read off window.backlit, which the
// bundle footer binds only AFTER this module evaluates.
type Nav = (returnPath?: string) => void;
let loginFn: Nav = () => {};
let logoutFn: Nav = () => {};

// configureFAB wires the login/logout navigations. Called once from index.ts.
export function configureFAB(login: Nav, logout: Nav): void {
  loginFn = login;
  logoutFn = logout;
}

// initFAB is the auto-inject entry point. Guards: non-browser (no document/
// window) and off-host both no-op. Defers to DOMContentLoaded when the bundle
// loaded in <head> before <body> existed.
export function initFAB(): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  if (!isBacklitHost()) return;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => void render(), { once: true });
  } else {
    void render();
  }
}

// hideUI removes the FAB — honoured only when the bootstrap says the owner may
// hide branding (a free author's call is a silent no-op). Race-safe: sets
// hideRequested so a call before the FAB injects still suppresses it (for a paid
// author); removes the live node when one is present.
export function removeFAB(): void {
  hideRequested = true;
  if (!lastCanHide) return; // free / not-yet-resolved author: no-op
  if (typeof document === "undefined") return;
  const el = document.getElementById(FAB_ROOT_ID);
  if (el && typeof el.remove === "function") el.remove();
}

async function render(): Promise<void> {
  if (typeof document === "undefined" || !document.body) return;
  if (document.getElementById(FAB_ROOT_ID)) return; // already injected (double-load guard)

  let cfg: UIConfig;
  try {
    cfg = await getBackend().uiConfig();
  } catch {
    return; // best-effort: no bootstrap → no FAB
  }
  lastCanHide = cfg.canHideBranding;
  if (cfg.brandingHidden) return; // per-glow paid toggle: don't inject at all
  if (hideRequested && cfg.canHideBranding) return; // hideUI() won the race (paid)
  inject(cfg);
}

// inject builds the FAB DOM inside a closed shadow root and appends it to body.
function inject(cfg: UIConfig): void {
  const body = document.body;
  if (!body) return;
  const host = document.createElement("div");
  host.setAttribute("id", FAB_ROOT_ID);
  host.style.cssText = "position:fixed;right:16px;bottom:16px;z-index:2147483647";

  const root = host.attachShadow({ mode: "closed" });

  const style = document.createElement("style");
  style.textContent = FAB_CSS;
  root.appendChild(style);

  // Toggle button: the Backlit mark on a dark disc with an amber glow behind it
  // (the CSS handles the glow; the SVG below is the logo). FAB_MARK_SVG is a
  // STATIC compile-time constant — no interpolation, no user data — so assigning
  // it via innerHTML does not touch the stored-XSS boundary the header documents
  // (which is only about dynamic, user-controlled strings). innerHTML is used
  // over createElementNS so the SVG parses into the real SVG namespace in the
  // browser while staying inert under the test DOM (which models no innerHTML).
  const toggle = document.createElement("button");
  toggle.setAttribute("type", "button");
  toggle.setAttribute("class", "fab-toggle");
  toggle.setAttribute("aria-label", "Backlit menu");
  toggle.setAttribute("aria-expanded", "false");
  toggle.innerHTML = FAB_MARK_SVG;

  // Panel (hidden until the toggle is clicked).
  const panel = document.createElement("div");
  panel.setAttribute("class", "fab-panel");
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "Backlit");
  panel.setAttribute("hidden", "");

  buildPanel(panel, cfg);

  let open = false;
  const setOpen = (v: boolean) => {
    open = v;
    toggle.setAttribute("aria-expanded", v ? "true" : "false");
    if (v) panel.removeAttribute("hidden");
    else panel.setAttribute("hidden", "");
  };
  toggle.addEventListener("click", () => setOpen(!open));
  // Escape closes the panel.
  root.addEventListener("keydown", (e: Event) => {
    if ((e as KeyboardEvent).key === "Escape") setOpen(false);
  });

  root.appendChild(panel);
  root.appendChild(toggle);
  body.appendChild(host);
}

// buildPanel fills the panel for the signed-in / anonymous states. All
// user-controlled strings go through textContent (XSS boundary).
function buildPanel(panel: HTMLElement, cfg: UIConfig): void {
  // The glow's display Title heads the panel when set (owner-controlled free
  // text → textContent, never innerHTML). Empty title → no heading.
  if (cfg.title) {
    const title = document.createElement("p");
    title.setAttribute("class", "fab-title");
    title.textContent = cfg.title;
    panel.appendChild(title);
  }

  if (cfg.user) {
    const who = document.createElement("p");
    who.setAttribute("class", "fab-who");
    // name || email — both user-controlled, rendered as text only.
    const label = cfg.user.name || cfg.user.email || "your account";
    who.textContent = "Signed in as ";
    const strong = document.createElement("strong");
    strong.textContent = label;
    who.appendChild(strong);
    panel.appendChild(who);

    const out = document.createElement("button");
    out.setAttribute("type", "button");
    out.setAttribute("class", "fab-btn");
    out.textContent = "Sign out";
    out.addEventListener("click", () => logoutFn());
    panel.appendChild(out);
  } else {
    const blurb = document.createElement("p");
    blurb.setAttribute("class", "fab-blurb");
    blurb.textContent = "This app is hosted on Backlit.";
    panel.appendChild(blurb);

    if (cfg.loginAvailable) {
      const inBtn = document.createElement("button");
      inBtn.setAttribute("type", "button");
      inBtn.setAttribute("class", "fab-btn");
      inBtn.textContent = "Sign in";
      inBtn.addEventListener("click", () => loginFn());
      panel.appendChild(inBtn);
    }
  }

  // Footer row: the "Get your own glow" link on the left, the Backlit lockup
  // on the right (matching the panel mockup). marketingUrl was https-validated
  // by the decoder; the lockup is the static FAB_LOCKUP_SVG (see inject()).
  const foot = document.createElement("div");
  foot.setAttribute("class", "fab-foot");

  const link = document.createElement("a");
  link.setAttribute("class", "fab-link");
  link.setAttribute("href", cfg.marketingUrl);
  link.setAttribute("target", "_blank");
  link.setAttribute("rel", "noopener noreferrer");
  link.textContent = "Get your own glow →";
  foot.appendChild(link);

  const brand = document.createElement("span");
  brand.setAttribute("class", "fab-brand");
  brand.setAttribute("aria-hidden", "true");
  brand.innerHTML = FAB_LOCKUP_SVG;
  foot.appendChild(brand);

  panel.appendChild(foot);
}

// FAB_MARK_SVG is the full-color Backlit primary mark (branding/svg/backlit-mark-primary.svg)
// — the amber back-panel (with its own soft bloom), the dark foreground card, and
// the three content lines. Inlined as a static string (see the innerHTML note in
// inject()); gradient/filter ids are prefixed so they can't collide even outside a
// shadow root. The button's aria-label names the control, so the mark itself is
// aria-hidden/decorative.
const FAB_MARK_SVG = `<svg class="fab-mark" viewBox="0 0 100 100" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="bl-fab-amber" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f4c06a"/><stop offset=".5" stop-color="#e29a3c"/><stop offset="1" stop-color="#b06a18"/></linearGradient><linearGradient id="bl-fab-dark" x1="0" y1="0" x2=".6" y2="1"><stop offset="0" stop-color="#1c1c25"/><stop offset="1" stop-color="#0c0c11"/></linearGradient><filter id="bl-fab-glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="5" result="b"/><feColorMatrix in="b" type="matrix" values="0 0 0 0 0.91  0 0 0 0 0.66  0 0 0 0 0.29  0 0 0 0.85 0"/></filter></defs><rect x="21" y="6" width="73" height="73" rx="14" fill="#e8a94a" filter="url(#bl-fab-glow)"/><rect x="21" y="6" width="73" height="73" rx="14" fill="url(#bl-fab-amber)"/><rect x="6" y="22" width="73" height="67" rx="14" fill="url(#bl-fab-dark)" stroke="rgba(255,255,255,.12)" stroke-width="1"/><rect x="19" y="41" width="34" height="5" rx="2.5" fill="#f0b95e"/><rect x="19" y="53" width="22" height="5" rx="2.5" fill="#f0b95e" opacity=".62"/><rect x="19" y="65" width="28" height="5" rx="2.5" fill="#f0b95e" opacity=".34"/></svg>`;

// FAB_LOCKUP_SVG is the horizontal Backlit lockup (mark + "backlit" wordmark)
// rendered in the panel footer beside the marketing link. It uses the CREAM
// wordmark fill (#f0ede6 — branding/svg/backlit-lockup-horizontal.svg, the same
// variant auth-api uses on its dark pages) so the wordmark reads on the dark
// panel; the -ink variant's dark wordmark would be invisible here. Static
// constant (see the innerHTML note in inject()); gradient/filter ids are
// prefixed (bl-lk-*) so they can't collide with the toggle mark's bl-fab-* ids
// in the shared shadow root.
const FAB_LOCKUP_SVG = `<svg class="fab-lockup" viewBox="0 0 279 96" fill="none" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="bl-lk-amber" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f4c06a"/><stop offset=".5" stop-color="#e29a3c"/><stop offset="1" stop-color="#b06a18"/></linearGradient><linearGradient id="bl-lk-dark" x1="0" y1="0" x2=".6" y2="1"><stop offset="0" stop-color="#1c1c25"/><stop offset="1" stop-color="#0c0c11"/></linearGradient><filter id="bl-lk-glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="5" result="b"/><feColorMatrix in="b" type="matrix" values="0 0 0 0 0.91  0 0 0 0 0.66  0 0 0 0 0.29  0 0 0 0.85 0"/></filter></defs><g transform="translate(0,8) scale(0.80)"><rect x="21" y="6" width="73" height="73" rx="14" fill="#e8a94a" filter="url(#bl-lk-glow)"/><rect x="21" y="6" width="73" height="73" rx="14" fill="url(#bl-lk-amber)"/><rect x="6" y="22" width="73" height="67" rx="14" fill="url(#bl-lk-dark)" stroke="rgba(255,255,255,0.12)" stroke-width="1"/><rect x="19" y="41" width="34" height="5" rx="2.5" fill="#f0b95e"/><rect x="19" y="53" width="22" height="5" rx="2.5" fill="#f0b95e" opacity="0.62"/><rect x="19" y="65" width="28" height="5" rx="2.5" fill="#f0b95e" opacity="0.34"/></g><g transform="translate(96,66) scale(0.056,-0.056)" fill="#f0ede6"><path transform="translate(0,0)" d="M344 -12Q303 -12 268 0Q233 12 205 34.5Q177 57 157 89L153 89L146 0L45 0L45 700L165 700L165 427Q190 459 233 483.5Q276 508 337 508Q412 508 465.5 474.5Q519 441 547.5 382Q576 323 576 248Q576 173 547.5 114Q519 55 466.5 21.5Q414 -12 344 -12ZM310 87Q354 87 387 108Q420 129 437 165Q454 201 454 249Q454 296 437 332Q420 368 387 388.5Q354 409 310 409Q266 409 233 388.5Q200 368 181.5 332Q163 296 163 248Q163 201 181.5 165Q200 129 233 108Q266 87 310 87Z"/><path transform="translate(576,0)" d="M203 -12Q145 -12 106 8.5Q67 29 48.5 63.5Q30 98 30 139Q30 186 54.5 220.5Q79 255 127 274.5Q175 294 246 294L374 294Q374 334 362 361Q350 388 326.5 401Q303 414 268 414Q226 414 196 394Q166 374 159 332L41 332Q47 387 77 426.5Q107 466 156.5 487Q206 508 268 508Q340 508 390.5 483.5Q441 459 467.5 414Q494 369 494 305L494 0L394 0L381 79L377 79Q363 56 344 39Q325 22 303.5 11Q282 0 256.5 -6Q231 -12 203 -12ZM239 83Q270 83 293.5 93.5Q317 104 333 123Q349 142 358.5 166.5Q368 191 370 217L370 217L259 217Q223 217 200.5 208Q178 199 167 184Q156 169 156 148Q156 127 166.5 112.5Q177 98 195.5 90.5Q214 83 239 83Z"/><path transform="translate(1080,0)" d="M282 -12Q203 -12 145.5 21Q88 54 56.5 112.5Q25 171 25 248Q25 324 58 383Q91 442 149 475Q207 508 282 508Q376 508 440.5 459Q505 410 522 325L396 325Q386 366 354.5 387.5Q323 409 281 409Q245 409 214.5 391Q184 373 165.5 336.5Q147 300 147 248Q147 210 157 180.5Q167 151 185.5 130.5Q204 110 228.5 99.5Q253 89 281 89Q309 89 332.5 98.5Q356 108 373 126.5Q390 145 396 171L522 171Q504 88 440 38Q376 -12 282 -12Z"/><path transform="translate(1601,0)" d="M371 0L147 273L345 496L488 496L241 224L241 317L520 0ZM45 0L45 700L165 700L165 0Z"/><path transform="translate(2102,0)" d="M45 0L45 700L165 700L165 0Z"/><path transform="translate(2287,0)" d="M56 0L56 496L176 496L176 0ZM116 569Q83 569 61.5 590Q40 611 40 641Q40 671 61.5 691.5Q83 712 116 712Q149 712 170.5 691.5Q192 671 192 641Q192 611 170.5 590Q149 569 116 569Z"/><path transform="translate(2495,0)" d="M257 0Q206 0 168.5 16Q131 32 110.5 69Q90 106 90 169L90 402L5 402L5 496L90 496L104 626L211 626L211 496L346 496L346 402L210 402L210 169Q210 128 226 113Q242 98 282 98L342 98L342 0Z"/></g><circle cx="265.0" cy="50" r="4.4" fill="#e8a94a"/></svg>`;

const FAB_CSS = `
:host { all: initial; }
.fab-toggle {
  width: 44px; height: 44px; border-radius: 9999px; border: none; cursor: pointer;
  padding: 0; display: grid; place-items: center;
  background: radial-gradient(circle at 50% 40%, #16161f, #0b0b10);
  /* Resting amber glow (minimum). Hover expands it to maximum below. */
  box-shadow:
    0 0 0 1px rgba(232,169,74,.35),
    0 0 12px 1px rgba(232,169,74,.5),
    0 0 24px 4px rgba(232,169,74,.26),
    0 4px 14px rgba(0,0,0,.45);
  transition: box-shadow .25s ease;
  -webkit-tap-highlight-color: transparent;
}
.fab-toggle:hover {
  box-shadow:
    0 0 0 1px rgba(232,169,74,.5),
    0 0 20px 3px rgba(232,169,74,.72),
    0 0 40px 9px rgba(232,169,74,.44),
    0 4px 14px rgba(0,0,0,.45);
}
.fab-toggle:focus-visible { outline: 2px solid #f4c06a; outline-offset: 3px; }
.fab-mark { width: 26px; height: 26px; display: block; pointer-events: none; }
.fab-panel {
  position: absolute; right: 0; bottom: 54px; width: 250px;
  background: #15161b; color: #e6e8ec; border: 1px solid #2a2c34; border-radius: 10px;
  padding: 12px; font: 13px/1.5 system-ui, sans-serif; box-shadow: 0 8px 24px rgba(0,0,0,.4);
}
.fab-panel[hidden] { display: none; }
.fab-title {
  margin: 0 0 10px; padding: 0 0 8px; border-bottom: 1px solid #2a2c34;
  font-size: 14px; font-weight: 600; color: #f0ede6; word-break: break-word;
}
.fab-who, .fab-blurb { margin: 0 0 8px; }
.fab-btn {
  display: block; width: 100%; margin: 0 0 8px; padding: 6px 10px; cursor: pointer;
  background: #23252e; color: #e6e8ec; border: 1px solid #34373f; border-radius: 6px;
  font: 13px system-ui, sans-serif;
}
.fab-btn:hover { background: #2c2f3a; }
.fab-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.fab-link { display: inline-block; color: #e8a94a; text-decoration: none; font-size: 12px; }
.fab-link:hover { text-decoration: underline; }
.fab-brand { flex: none; display: inline-flex; align-items: center; }
.fab-lockup { display: block; height: 20px; width: auto; margin-top: 3px; }
`;

// Test-only exports (not part of the public window.backlit surface). resetFAB
// clears the module-level state so each test starts clean.
export function _resetFABForTest(): void {
  hideRequested = false;
  lastCanHide = false;
  loginFn = () => {};
  logoutFn = () => {};
}
// _renderForTest runs the bootstrap-fetch + inject flow and resolves when it
// settles, so tests don't race the async injection.
export function _renderForTest(): Promise<void> {
  return render();
}
export { FAB_ROOT_ID as _FAB_ROOT_ID };
